<script setup>
// SiteUp — Modo Foco, Parte 3: hub de entrada. Home enxuta por onde a pessoa
// passa antes de ir pro trabalho: saudação + cartões com o essencial do dia.
// Linguagem visual do design system estilo X (skill siteup-x-design-system).
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountId, accountScopedRoute } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const conversationStats = useMapGetter('conversationStats/getStats');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const firstName = computed(() => {
  const name =
    currentUser.value?.available_name || currentUser.value?.name || '';
  return name.split(' ')[0];
});

const greeting = computed(() => {
  const hour = new Date().getHours();
  if (hour < 12) return t('SIDEBAR.FOCUS_MODE.HUB_GOOD_MORNING');
  if (hour < 18) return t('SIDEBAR.FOCUS_MODE.HUB_GOOD_AFTERNOON');
  return t('SIDEBAR.FOCUS_MODE.HUB_GOOD_EVENING');
});

const mineCount = computed(() => conversationStats.value?.mineCount || 0);
const allCount = computed(() => conversationStats.value?.allCount || 0);

const hasKanban = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.KANBAN_BOARD)
);

const goTo = name => router.push(accountScopedRoute(name));

onMounted(() => {
  store.dispatch('conversationStats/get', { assigneeType: 'me' });
});
</script>

<template>
  <div
    class="hub flex flex-1 min-h-0 w-full flex-col items-center justify-center gap-8 overflow-y-auto bg-n-background px-6 py-12"
  >
    <div class="hub-greet text-center">
      <h1
        class="m-0 mb-1.5 text-[26px] font-extrabold tracking-tight text-n-slate-12"
      >
        {{ greeting }}, {{ firstName }}
      </h1>
      <p class="m-0 text-[15px] text-n-slate-11">
        <template v-if="mineCount">
          {{
            t('SIDEBAR.FOCUS_MODE.HUB_WAITING', mineCount, {
              count: mineCount,
            })
          }}
        </template>
        <template v-else>
          {{ t('SIDEBAR.FOCUS_MODE.HUB_ALL_CLEAR') }}
        </template>
      </p>
    </div>

    <div class="hub-grid grid w-full max-w-3xl gap-3.5 sm:grid-cols-3">
      <button
        type="button"
        class="hub-card hub-card--primary"
        @click="goTo('home')"
      >
        <span v-if="mineCount" class="hub-count">{{ mineCount }}</span>
        <span class="hub-ico"><span class="i-ri-chat-1-line size-5" /></span>
        <span class="hub-name">{{ t('SIDEBAR.CONVERSATIONS') }}</span>
        <span class="hub-meta">
          {{
            t('SIDEBAR.FOCUS_MODE.HUB_CONVERSATIONS_META', allCount, {
              count: allCount,
            })
          }}
        </span>
      </button>

      <button
        v-if="hasKanban"
        type="button"
        class="hub-card"
        @click="goTo('kanban_board')"
      >
        <span class="hub-ico"
          ><span class="i-ri-layout-grid-line size-5"
        /></span>
        <span class="hub-name">{{ 'Kanban' }}</span>
        <span class="hub-meta">{{
          t('SIDEBAR.FOCUS_MODE.HUB_KANBAN_META')
        }}</span>
      </button>

      <button type="button" class="hub-card" @click="goTo('tasks')">
        <span class="hub-ico"><span class="i-ri-checkbox-line size-5" /></span>
        <span class="hub-name">{{ 'Tarefas' }}</span>
        <span class="hub-meta">{{
          t('SIDEBAR.FOCUS_MODE.HUB_TASKS_META')
        }}</span>
      </button>
    </div>

    <p class="hub-foot m-0 text-[13px] text-n-slate-10">
      {{ t('SIDEBAR.FOCUS_MODE.HUB_FOOTNOTE') }}
    </p>
  </div>
</template>

<style scoped>
.hub-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1.25rem;
  text-align: start;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--border-weak));
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    border-color var(--motion-duration-hover) var(--motion-ease-out),
    transform var(--motion-duration-press) var(--motion-ease-out);
}
.hub-card:hover {
  background: rgb(var(--solid-2));
}
.hub-card:active {
  transform: scale(0.98);
}
.hub-card--primary {
  background: rgb(var(--slate-12));
  border-color: rgb(var(--slate-12));
}
.hub-card--primary:hover {
  background: rgb(var(--slate-12) / 0.9);
}
.hub-card--primary .hub-name {
  color: rgb(var(--background-color));
}
.hub-card--primary .hub-meta {
  color: rgb(var(--background-color) / 0.6);
}
.hub-card--primary .hub-ico {
  background: rgb(var(--background-color) / 0.1);
  color: rgb(var(--background-color));
}
.hub-ico {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.375rem;
  height: 2.375rem;
  border-radius: 9999px;
  background: rgb(var(--solid-3));
  color: rgb(var(--slate-12));
}
.hub-name {
  font-size: 1rem;
  font-weight: 800;
  color: rgb(var(--slate-12));
}
.hub-meta {
  font-size: 0.8125rem;
  line-height: 1.4;
  color: rgb(var(--slate-11));
}
.hub-count {
  position: absolute;
  top: 0.875rem;
  inset-inline-end: 0.875rem;
  min-width: 1.375rem;
  height: 1.375rem;
  padding: 0 0.4375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgb(var(--blue-9));
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* entrada em cascata (tokens de motion) */
@media (prefers-reduced-motion: no-preference) {
  .hub-greet,
  .hub-card,
  .hub-foot {
    opacity: 0;
    animation: hub-rise var(--motion-duration-enter) var(--motion-ease-out)
      forwards;
  }
  .hub-grid .hub-card:nth-child(1) {
    animation-delay: 90ms;
  }
  .hub-grid .hub-card:nth-child(2) {
    animation-delay: calc(90ms + var(--motion-stagger-step));
  }
  .hub-grid .hub-card:nth-child(3) {
    animation-delay: calc(90ms + var(--motion-stagger-step) * 2);
  }
  .hub-foot {
    animation-delay: 280ms;
  }
}
@keyframes hub-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
